import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import type { AccessPath } from '@shared';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  concatMap,
  debounceTime,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import * as fromGenerated from '../../_generated';
import { type SharedWithType } from '../../analytics/analytics.events';
import * as fromHelper from '../../analytics/analytics.helper';
import { AnalyticsService } from '../../analytics/analytics.service';
import {
  AdminTeammateInvitee,
  AdminTeammatesActions,
  inviteeKey,
} from './admin-teammates.actions';

/**
 * Effects for the Admin Teammates surface. Reads: list + lazy dropdown payload.
 * Builder data: users + groups (on open). Writes: create (atomic skill-attach),
 * content update, delete, and the invitee grant/revoke fan-out — each leaf is one
 * endpoint with its own per-entity failure toast (no shared error path).
 */
@Injectable()
export class AdminTeammatesEffects {
  private readonly actions$ = inject(Actions);
  private readonly adminService = inject(fromGenerated.AdminService);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);
  private readonly analyticsService = inject(AnalyticsService);

  // ─── Reads ─────────────────────────────────────────────────────────────────
  loadTeammates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.loadTeammates),
      switchMap(() =>
        forkJoin([
          this.adminService.adminTeammatesControllerListV1(
            undefined,
            undefined,
            undefined,
            this.currentLocale(),
          ),
          timer(1000),
        ]).pipe(
          map(([teammates]) =>
            AdminTeammatesActions.loadTeammatesSuccess({ teammates }),
          ),
          catchError((error: Error) =>
            of(
              AdminTeammatesActions.loadTeammatesFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadTeammatesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.loadTeammatesFailure),
        tap(() => this.toastError('ADMIN.TEAMMATES.TOAST.LOAD_ERROR')),
      ),
    { dispatch: false },
  );

  loadDropdownPayload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.loadDropdownPayload),
      concatMap(({ teammateId }) =>
        this.adminService
          .adminTeammatesControllerGetDropdownPayloadV1(
            teammateId,
            this.currentLocale(),
          )
          .pipe(
            map((payload) =>
              AdminTeammatesActions.loadDropdownPayloadSuccess({
                teammateId,
                payload,
              }),
            ),
            catchError((error: Error) =>
              of(
                AdminTeammatesActions.loadDropdownPayloadFailure({
                  teammateId,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadDropdownPayloadFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.loadDropdownPayloadFailure),
        tap(() =>
          this.toastError('ADMIN.TEAMMATES.TOAST.DROPDOWN_PAYLOAD_ERROR'),
        ),
      ),
    { dispatch: false },
  );

  // ─── Builder data (fired when the Builder opens) ─────────────────────────────
  loadBuilderData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AdminTeammatesActions.openCreateModal,
        AdminTeammatesActions.openEditModal,
      ),
      mergeMap(() => [
        AdminTeammatesActions.loadAllUsers(),
        AdminTeammatesActions.loadAllGroups(),
      ]),
    ),
  );

  loadAllUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.loadAllUsers),
      switchMap(() =>
        this.adminService.adminControllerGetUsersV1().pipe(
          map((users) => AdminTeammatesActions.loadAllUsersSuccess({ users })),
          // A fetch error must stay distinct from "no users exist" — mapping it
          // to an empty success would hide the failure and leave the invite
          // typeahead silently empty.
          catchError((error: Error) =>
            of(
              AdminTeammatesActions.loadAllUsersFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadAllUsersFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.loadAllUsersFailure),
        tap(() => this.toastError('ADMIN.TEAMMATES.TOAST.LOAD_USERS_ERROR')),
      ),
    { dispatch: false },
  );

  loadAllGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.loadAllGroups),
      switchMap(() =>
        this.adminService.adminGroupsControllerListV1().pipe(
          map((groups) =>
            AdminTeammatesActions.loadAllGroupsSuccess({ groups }),
          ),
          // Keep a failure distinct from "no groups exist": an empty success
          // would leave allGroupId null and the org-wide grant path would
          // silently no-op with nothing surfaced to the user.
          catchError((error: Error) =>
            of(
              AdminTeammatesActions.loadAllGroupsFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadAllGroupsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.loadAllGroupsFailure),
        tap(() => this.toastError('ADMIN.TEAMMATES.TOAST.LOAD_GROUPS_ERROR')),
      ),
    { dispatch: false },
  );

  // Edit open: seed staged invitees (from the access graph) + attached skills
  // (from the dropdown payload). One forkJoin, two seed actions.
  openEditModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.openEditModal),
      mergeMap(({ teammate }) =>
        forkJoin([
          this.adminService.adminAccessControllerGetAccessByTeammateV1(
            teammate.id,
          ),
          this.adminService.adminTeammatesControllerGetDropdownPayloadV1(
            teammate.id,
            this.currentLocale(),
          ),
        ]).pipe(
          mergeMap(([access, payload]) => {
            const invitees: AdminTeammateInvitee[] = [
              ...access.users.map((u) => ({
                kind: 'user' as const,
                id: u.id,
                displayName: u.displayName,
                secondary: u.email,
              })),
              ...access.groups.map((g) => ({
                kind: 'group' as const,
                id: g.id,
                displayName: g.displayName,
              })),
            ];
            return [
              AdminTeammatesActions.seedEditInvitees({ invitees }),
              AdminTeammatesActions.seedEditSkills({
                skillIds: payload.skills.items.map((s) => s.id),
              }),
              // Cache the payload so the Builder can resolve attached-skill names
              // and the row's count cells expand without a second fetch.
              AdminTeammatesActions.loadDropdownPayloadSuccess({
                teammateId: teammate.id,
                payload,
              }),
            ];
          }),
          // A failed seed must not leave the Builder open showing an empty
          // editor for a teammate that actually has access/skills (and risking a
          // diff-based save against the wrong baseline): surface the error and
          // close the Builder so the admin reopens once the fetch recovers.
          catchError((error: Error) =>
            of(
              AdminTeammatesActions.loadDropdownPayloadFailure({
                teammateId: teammate.id,
                error: error.message,
              }),
              AdminTeammatesActions.closeModal(),
            ),
          ),
        ),
      ),
    ),
  );

  // ─── Skills typeahead (debounced, cancel-on-newer) ───────────────────────────
  searchSkills$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.searchSkills),
      debounceTime(250),
      switchMap(({ query }) =>
        this.adminService
          .adminSkillsControllerListV1(query, undefined, this.currentLocale())
          .pipe(
            map((results) =>
              AdminTeammatesActions.searchSkillsSuccess({ results }),
            ),
            catchError((error: Error) =>
              of(
                AdminTeammatesActions.searchSkillsFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  // ─── Create (teammate + skills atomic; invitees fan out after) ───────────────
  createTeammate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.createTeammate),
      switchMap(({ body, accessMode, invitees, allGroupId }) =>
        this.adminService.adminTeammatesControllerCreateShareableV1(body).pipe(
          map((teammate) =>
            AdminTeammatesActions.createTeammateSuccess({
              teammate,
              accessMode,
              invitees,
              allGroupId,
            }),
          ),
          catchError((error: Error) =>
            of(
              AdminTeammatesActions.createTeammateFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // Pure dispatcher: one grant per staged invitee (or one Group(all) for org-wide).
  // Also registers the KD-5 aggregation batch, so `teammate_shared` fires when a
  // teammate is *created* with sharing already configured — not only when sharing
  // is edited afterwards (`initializeAggregation$` covers the edit path only).
  afterCreateTeammateFanOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.createTeammateSuccess),
      mergeMap(({ teammate, accessMode, invitees, allGroupId }) => {
        const batchId = uuidv4(); // Correlation ID for aggregation (KD-5)
        const actions: Action[] = [];
        if (accessMode === 'org-wide') {
          // Gated on the mode flag, then on the id being known — never on the
          // id's truthiness alone (the P0 #2 shape on the edit path).
          if (allGroupId) {
            actions.push(
              AdminTeammatesActions.grantGroupTeammateAccess({
                batchId,
                teammateId: teammate.id,
                groupId: allGroupId,
                groupDisplayName: this.orgWideLabel(),
              }),
            );
          }
        } else {
          for (const invitee of invitees) {
            actions.push(this.grantFor(batchId, teammate.id, invitee));
          }
        }

        // `totalExpected` counts the actions actually dispatched rather than
        // recomputing from invitees/allGroupId, so the two cannot disagree: an
        // independently-derived count that overshoots (org-wide with no
        // allGroupId dispatches nothing) would strand the batch in
        // `aggregationMap` forever, never reaching totalExpected and never
        // firing. Registering here — synchronously, before the returned actions
        // are emitted — is what lets the grant/revoke trackers find the batch.
        if (actions.length > 0) {
          this.aggregationMap.set(batchId, {
            batchId,
            teammate,
            totalExpected: actions.length,
            succeeded: [],
            completed: 0,
          });
        }

        return actions;
      }),
    ),
  );

  createTeammateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.createTeammateSuccess),
        tap(() => this.toastSuccess('ADMIN.TEAMMATES.TOAST.CREATE_SUCCESS')),
      ),
    { dispatch: false },
  );

  createTeammateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.createTeammateFailure),
        tap(() => this.toastActionError('ADMIN.TEAMMATES.TOAST.CREATE_ERROR')),
      ),
    { dispatch: false },
  );

  // ─── Update content (super_admin) ────────────────────────────────────────────
  updateTeammateContent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.updateTeammateContent),
      switchMap(({ teammateId, body }) =>
        this.adminService
          .adminTeammatesControllerUpdateV1(teammateId, body)
          .pipe(
            map((teammate) =>
              AdminTeammatesActions.updateTeammateContentSuccess({ teammate }),
            ),
            catchError((error: Error) =>
              of(
                AdminTeammatesActions.updateTeammateContentFailure({
                  teammateId,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  updateTeammateContentSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.updateTeammateContentSuccess),
        tap(() =>
          this.toastSuccess('ADMIN.TEAMMATES.TOAST.UPDATE_CONTENT_SUCCESS'),
        ),
      ),
    { dispatch: false },
  );

  updateTeammateContentFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.updateTeammateContentFailure),
        tap(() =>
          this.toastActionError('ADMIN.TEAMMATES.TOAST.UPDATE_CONTENT_ERROR'),
        ),
      ),
    { dispatch: false },
  );

  // ─── Delete ──────────────────────────────────────────────────────────────────
  deleteTeammate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.deleteTeammate),
      switchMap(({ teammateId, teammate }) =>
        this.adminService.adminTeammatesControllerDeleteV1(teammateId).pipe(
          map(() =>
            AdminTeammatesActions.deleteTeammateSuccess({
              teammateId,
              teammate,
            }),
          ),
          catchError((error: Error) =>
            of(
              AdminTeammatesActions.deleteTeammateFailure({
                teammateId,
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  deleteTeammateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.deleteTeammateSuccess),
        tap(() => this.toastSuccess('ADMIN.TEAMMATES.TOAST.DELETE_SUCCESS')),
      ),
    { dispatch: false },
  );

  deleteTeammateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.deleteTeammateFailure),
        tap(() => this.toastActionError('ADMIN.TEAMMATES.TOAST.DELETE_ERROR')),
      ),
    { dispatch: false },
  );

  // ─── Analytics Effects (fire on success, non-blocking) ─────────────────────

  teammateCreatedAnalytics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.createTeammateSuccess),
        tap(({ teammate, accessMode }) => {
          this.analyticsService
            .sendTeammateCreatedEvent({
              teammate: {
                id: teammate.id,
                name: teammate.name,
                // `accessMode` is the Builder's domain value (hyphenated);
                // `sharingMode` is the payload value (underscored). The mapping
                // between the two spellings is deliberate — see
                // `TeammateForAnalytics`.
                sharingMode:
                  accessMode === 'org-wide'
                    ? ('org_wide' as const)
                    : ('invited_only' as const),
                // SB-3519 T8: the stored value, never the rendered one. This
                // literal exists instead of `toTeammateForAnalytics` only
                // because `sharingMode` above has a different source here.
                visualType: teammate.visualType,
              },
            })
            .subscribe();
        }),
      ),
    { dispatch: false },
  );

  teammateUpdatedAnalytics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.updateTeammateContentSuccess),
        tap(({ teammate }) => {
          const group = fromHelper.toGroupForAnalytics(
            teammate.accessPathGroup,
          );
          this.analyticsService
            .sendTeammateUpdatedEvent({
              teammate: fromHelper.toTeammateForAnalytics(teammate),
              ...(teammate.accessPath && {
                accessPath: teammate.accessPath as unknown as AccessPath,
                ...(group && { group }),
              }),
            })
            .subscribe();
        }),
      ),
    { dispatch: false },
  );

  teammateDeletedAnalytics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.deleteTeammateSuccess),
        tap(({ teammate }) => {
          const group = fromHelper.toGroupForAnalytics(
            teammate.accessPathGroup,
          );
          this.analyticsService
            .sendTeammateDeletedEvent({
              teammate: fromHelper.toTeammateForAnalytics(teammate),
              ...(teammate.accessPath && {
                accessPath: teammate.accessPath as unknown as AccessPath,
                ...(group && { group }),
              }),
            })
            .subscribe();
        }),
      ),
    { dispatch: false },
  );

  // ─── Invitee fan-out (pure dispatcher: one leaf per diff entry) ──────────────
  fanOutInviteeDiff$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.fanOutInviteeDiff),
      mergeMap(({ batchId, teammateId, adds, removes }) => {
        const actions: Action[] = [];
        // Add grants for new invitees
        for (const invitee of adds) {
          actions.push(this.grantFor(batchId, teammateId, invitee));
        }
        // Add revokes for removed invitees
        for (const invitee of removes) {
          actions.push(this.revokeFor(batchId, teammateId, invitee));
        }
        // Nothing is added here. Org-wide is already resolved upstream: the
        // dispatcher puts the all-group in `adds` when it is on and in `removes`
        // when it is off, and `grantFor`/`revokeFor` handle `kind: 'group'`.
        return actions;
      }),
    ),
  );

  grantUserTeammateAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.grantUserTeammateAccess),
      mergeMap(({ batchId, teammateId, userId, userDisplayName }) =>
        this.adminService
          .adminAccessControllerGrantUserToTeammateV1({ userId, teammateId })
          .pipe(
            map(() =>
              AdminTeammatesActions.grantUserTeammateAccessSuccess({
                batchId,
                teammateId,
                userId,
              }),
            ),
            catchError((error: Error) =>
              of(
                AdminTeammatesActions.grantUserTeammateAccessFailure({
                  batchId,
                  teammateId,
                  userId,
                  userDisplayName,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  revokeUserTeammateAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.revokeUserTeammateAccess),
      mergeMap(({ batchId, teammateId, userId, userDisplayName }) =>
        this.adminService
          .adminAccessControllerRevokeUserFromTeammateV1(userId, teammateId)
          .pipe(
            map(() =>
              AdminTeammatesActions.revokeUserTeammateAccessSuccess({
                batchId,
                teammateId,
                userId,
              }),
            ),
            catchError((error: Error) =>
              of(
                AdminTeammatesActions.revokeUserTeammateAccessFailure({
                  batchId,
                  teammateId,
                  userId,
                  userDisplayName,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  grantGroupTeammateAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.grantGroupTeammateAccess),
      mergeMap(({ batchId, teammateId, groupId, groupDisplayName }) =>
        this.adminService
          .adminAccessControllerGrantGroupToTeammateV1({ groupId, teammateId })
          .pipe(
            map(() =>
              AdminTeammatesActions.grantGroupTeammateAccessSuccess({
                batchId,
                teammateId,
                groupId,
              }),
            ),
            catchError((error: Error) =>
              of(
                AdminTeammatesActions.grantGroupTeammateAccessFailure({
                  batchId,
                  teammateId,
                  groupId,
                  groupDisplayName,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  revokeGroupTeammateAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.revokeGroupTeammateAccess),
      mergeMap(({ batchId, teammateId, groupId, groupDisplayName }) =>
        this.adminService
          .adminAccessControllerRevokeGroupFromTeammateV1(groupId, teammateId)
          .pipe(
            map(() =>
              AdminTeammatesActions.revokeGroupTeammateAccessSuccess({
                batchId,
                teammateId,
                groupId,
              }),
            ),
            catchError((error: Error) =>
              of(
                AdminTeammatesActions.revokeGroupTeammateAccessFailure({
                  batchId,
                  teammateId,
                  groupId,
                  groupDisplayName,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  // ─── Teammate shared aggregation (fires once per batch with succeeded subset) ─
  /**
   * Aggregates all grant/revoke operations for a batch, fires teammate_shared
   * event with succeeded subset only (KD-5 partial-failure semantics).
   * Keyed on batchId (not just teammateId) to handle concurrent saves correctly
   * (Scenario 7: concurrent saves for same teammate must fire separate events).
   */
  private readonly aggregationMap = new Map<
    string,
    {
      batchId: string;
      teammate: fromGenerated.AdminTeammateView;
      totalExpected: number;
      /**
       * The share set before this batch. Optional because the create path has
       * no before-set — a teammate created with sharing configured starts from
       * nothing, so its final state is exactly its succeeded grants.
       */
      initialInvitees?: AdminTeammateInvitee[];
      /**
       * Settled operations that actually took effect. `op` is recorded because
       * the final state needs both directions: a succeeded revoke removes from
       * the before-set just as a succeeded grant adds to it. Failures are never
       * pushed here (KD-5 partial-failure semantics).
       */
      succeeded: { kind: 'user' | 'group'; id: string; op: 'add' | 'remove' }[];
      completed: number;
    }
  >();

  // Listen for the fan-out dispatch to initialize aggregation state
  initializeAggregation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.fanOutInviteeDiff),
        tap(({ batchId, teammate, initialInvitees, adds, removes }) => {
          // Expected action count is exactly what the fan-out dispatches — one
          // per add and one per remove, the all-group included. Reserving an
          // extra slot for an org-wide grant would leave `completed` short of
          // `totalExpected` forever, and teammate_shared would never fire.
          const expectedCount = adds.length + removes.length;

          if (expectedCount > 0) {
            this.aggregationMap.set(batchId, {
              batchId,
              teammate,
              totalExpected: expectedCount,
              initialInvitees,
              succeeded: [],
              completed: 0,
            });
          }
        }),
      ),
    { dispatch: false },
  );

  // Track successful grant/revoke and check if batch is complete
  trackGrantSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.grantUserTeammateAccessSuccess),
        tap(({ batchId, userId }) => {
          const batch = this.aggregationMap.get(batchId);
          if (batch) {
            batch.succeeded.push({ kind: 'user', id: userId, op: 'add' });
            batch.completed++;
            this.maybeFireTeammateSharedEvent(batchId);
          }
        }),
      ),
    { dispatch: false },
  );

  trackRevokeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.revokeUserTeammateAccessSuccess),
        tap(({ batchId, userId }) => {
          const batch = this.aggregationMap.get(batchId);
          if (batch) {
            batch.succeeded.push({ kind: 'user', id: userId, op: 'remove' });
            batch.completed++;
            this.maybeFireTeammateSharedEvent(batchId);
          }
        }),
      ),
    { dispatch: false },
  );

  trackGrantGroupSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.grantGroupTeammateAccessSuccess),
        tap(({ batchId, groupId }) => {
          const batch = this.aggregationMap.get(batchId);
          if (batch) {
            batch.succeeded.push({ kind: 'group', id: groupId, op: 'add' });
            batch.completed++;
            this.maybeFireTeammateSharedEvent(batchId);
          }
        }),
      ),
    { dispatch: false },
  );

  trackRevokeGroupSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.revokeGroupTeammateAccessSuccess),
        tap(({ batchId, groupId }) => {
          const batch = this.aggregationMap.get(batchId);
          if (batch) {
            batch.succeeded.push({ kind: 'group', id: groupId, op: 'remove' });
            batch.completed++;
            this.maybeFireTeammateSharedEvent(batchId);
          }
        }),
      ),
    { dispatch: false },
  );

  trackGrantFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.grantUserTeammateAccessFailure),
        tap(({ batchId }) => {
          const batch = this.aggregationMap.get(batchId);
          if (batch) {
            batch.completed++;
            // Failed grant: NOT added to succeeded list (KD-5 partial-failure semantics)
            this.maybeFireTeammateSharedEvent(batchId);
          }
        }),
      ),
    { dispatch: false },
  );

  trackRevokeFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.revokeUserTeammateAccessFailure),
        tap(({ batchId }) => {
          const batch = this.aggregationMap.get(batchId);
          if (batch) {
            batch.completed++;
            this.maybeFireTeammateSharedEvent(batchId);
          }
        }),
      ),
    { dispatch: false },
  );

  trackGrantGroupFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.grantGroupTeammateAccessFailure),
        tap(({ batchId }) => {
          const batch = this.aggregationMap.get(batchId);
          if (batch) {
            batch.completed++;
            this.maybeFireTeammateSharedEvent(batchId);
          }
        }),
      ),
    { dispatch: false },
  );

  trackRevokeGroupFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.revokeGroupTeammateAccessFailure),
        tap(({ batchId }) => {
          const batch = this.aggregationMap.get(batchId);
          if (batch) {
            batch.completed++;
            this.maybeFireTeammateSharedEvent(batchId);
          }
        }),
      ),
    { dispatch: false },
  );

  /**
   * Fire teammate_shared event when all actions for a batch have settled.
   * Payload reflects the share set as it actually stands afterwards: the
   * before-set with this batch's *succeeded* grants and revokes applied, so a
   * failed operation is absent from the total rather than assumed (KD-5).
   * Fire-and-forget: never blocks, errors logged to Sentry.
   */
  private maybeFireTeammateSharedEvent(batchId: string): void {
    const batch = this.aggregationMap.get(batchId);
    if (!batch || batch.completed < batch.totalExpected) {
      return; // Still waiting for more actions
    }

    // Final state, not this batch's delta. Counting only succeeded *grants*
    // reported 0 for a pure-revoke batch — a teammate that demonstrably still
    // had shares — and contradicted the property's own "FINAL STATE" contract.
    const finalSet = new Map<string, 'user' | 'group'>(
      (batch.initialInvitees ?? []).map((i) => [inviteeKey(i), i.kind]),
    );
    for (const settled of batch.succeeded) {
      if (settled.op === 'add') {
        finalSet.set(inviteeKey(settled), settled.kind);
      } else {
        finalSet.delete(inviteeKey(settled));
      }
    }

    const kinds = [...finalSet.values()];
    const userCount = kinds.filter((kind) => kind === 'user').length;
    const groupCount = kinds.filter((kind) => kind === 'group').length;
    const sharedWithCount = userCount + groupCount;

    // An org-wide teammate's final set is the single Group(all), so it reports
    // `group`; `teammate.sharingMode: 'org_wide'` in the same payload carries
    // the org-wide-ness.
    let sharedWithType: SharedWithType;
    if (sharedWithCount === 0) {
      sharedWithType = 'none';
    } else if (groupCount === 0) {
      sharedWithType = 'user';
    } else if (userCount === 0) {
      sharedWithType = 'group';
    } else {
      sharedWithType = 'array'; // Mixed
    }

    const sharedGroup = fromHelper.toGroupForAnalytics(
      batch.teammate.accessPathGroup,
    );
    this.analyticsService
      .sendTeammateSharedEvent({
        teammate: fromHelper.toTeammateForAnalytics(batch.teammate),
        ...(batch.teammate.accessPath && {
          accessPath: batch.teammate.accessPath as unknown as AccessPath,
          ...(sharedGroup && { group: sharedGroup }),
        }),
        sharedWithType,
        sharedWithCount,
      })
      .subscribe({
        next: () => {
          // Success: clean up aggregation state
          this.aggregationMap.delete(batchId);
        },
        error: () => {
          // Failure logged to Sentry via catchError in service
          // Still clean up aggregation state (non-blocking per design)
          this.aggregationMap.delete(batchId);
        },
      });
  }

  // ─── Per-entity failure toasts (name the failed user/group) ──────────────────
  grantUserFailure$ = this.failureToast(
    AdminTeammatesActions.grantUserTeammateAccessFailure,
    'ADMIN.TEAMMATES.TOAST.GRANT_USER_ACCESS_ERROR',
    'user',
  );
  revokeUserFailure$ = this.failureToast(
    AdminTeammatesActions.revokeUserTeammateAccessFailure,
    'ADMIN.TEAMMATES.TOAST.REVOKE_USER_ACCESS_ERROR',
    'user',
  );
  grantGroupFailure$ = this.failureToast(
    AdminTeammatesActions.grantGroupTeammateAccessFailure,
    'ADMIN.TEAMMATES.TOAST.GRANT_GROUP_ACCESS_ERROR',
    'group',
  );
  revokeGroupFailure$ = this.failureToast(
    AdminTeammatesActions.revokeGroupTeammateAccessFailure,
    'ADMIN.TEAMMATES.TOAST.REVOKE_GROUP_ACCESS_ERROR',
    'group',
  );

  private grantFor(
    batchId: string,
    teammateId: string,
    invitee: AdminTeammateInvitee,
  ): Action {
    return invitee.kind === 'user'
      ? AdminTeammatesActions.grantUserTeammateAccess({
          batchId,
          teammateId,
          userId: invitee.id,
          userDisplayName: invitee.displayName,
        })
      : AdminTeammatesActions.grantGroupTeammateAccess({
          batchId,
          teammateId,
          groupId: invitee.id,
          groupDisplayName: invitee.displayName,
        });
  }

  private revokeFor(
    batchId: string,
    teammateId: string,
    invitee: AdminTeammateInvitee,
  ): Action {
    return invitee.kind === 'user'
      ? AdminTeammatesActions.revokeUserTeammateAccess({
          batchId,
          teammateId,
          userId: invitee.id,
          userDisplayName: invitee.displayName,
        })
      : AdminTeammatesActions.revokeGroupTeammateAccess({
          batchId,
          teammateId,
          groupId: invitee.id,
          groupDisplayName: invitee.displayName,
        });
  }

  private orgWideLabel(): string {
    return this.translateService.instant(
      'ADMIN.TEAMMATES.TABLE.USERS_ORG_WIDE',
    ) as string;
  }

  /**
   * Admin reads resolve localized teammate/skill names server-side, so every
   * request that accepts a locale gets the viewer's current UI language.
   */
  private currentLocale(): 'en' | 'fr' {
    // The app registers only en + fr; narrow at runtime (default en) rather than
    // casting, so an unexpected lang can't reach the API as an invalid locale.
    return this.translateService.getCurrentLang() === 'fr' ? 'fr' : 'en';
  }

  /** Build a dispatch:false toast effect that names the failed entity. */
  private failureToast(
    action: { type: string },
    key: string,
    interpolationKey: 'user' | 'group',
  ) {
    return createEffect(
      () =>
        this.actions$.pipe(
          ofType(action as never),
          tap(
            (payload: {
              userDisplayName?: string;
              groupDisplayName?: string;
            }) =>
              this.toastrService.error(
                this.translateService.instant(key, {
                  [interpolationKey]:
                    payload.userDisplayName ?? payload.groupDisplayName ?? '',
                }) as string,
                undefined,
                { closeButton: true },
              ),
          ),
        ),
      { dispatch: false },
    );
  }

  private toastSuccess(key: string): void {
    this.toastrService.success(
      this.translateService.instant(key) as string,
      undefined,
      { closeButton: true },
    );
  }

  private toastError(key: string): void {
    this.toastrService.error(
      this.translateService.instant(key) as string,
      undefined,
      { closeButton: true },
    );
  }

  // A failed create, update or delete stays until it is closed, as on the
  // Groups page and in the skill builder.
  private toastActionError(key: string): void {
    this.toastrService.error(
      this.translateService.instant(key) as string,
      undefined,
      { closeButton: true, disableTimeOut: true },
    );
  }
}
